// Copyright (c) StrangeBee 2023

import { Flatten } from '@Types';
import { flatten } from 'flat';

const loginRoot = 'login';
const caseRoot = 'cases';
const administrationRoot = 'administration';
const alertRoot = 'alerts';
const myTasksRoot = 'my-tasks';
const waitingTasksRoot = 'waiting-tasks';
const accountRoot = 'account';
const dashboardsRoot = 'dashboards';
const searchRoot = 'search';
const liveRoot = 'live';
const globalTasksRoot = 'global-tasks';

const casePattern = ':caseId';
const organisationPattern = ':orgId';
const alertPattern = ':alertId';
const dashboardPattern = ':dashboardId';
const observablePattern = ':observableId';
const pagePattern = ':pageId';
const taskPattern = ':taskId';
const viewsPattern = ':viewId';
const sectionPattern = ':sectionId';
const catalogPattern = ':catalogId';
const patternSecret = ':secret';
const reportsPattern = ':reportId';
const reportTemplatePattern = ':reportTemplateId';

const alertStatusChild = 'alert-status';
const caseStatusChild = 'case-status';
const analyzerTemplates = 'analyzer-templates';
const attachmentsChild = 'attachments';
const attackPatterns = 'attack-patterns';
const authenticationChild = 'authentication';
const brandingChild = 'branding';
const caseTemplatesChild = 'caseTemplates';
const changeChild = 'reset-password';
const commentsChild = 'comments';
const cortexChild = 'cortex';
const customFieldsChild = 'custom-fields';
const customTagsChild = 'customTags';
const detailsChild = 'details';
const entitiesManagementChild = 'entities';
const functionsChild = 'functions';
const globalUsersChild = 'users';
const licenseChild = 'license';
const historyChild = 'history';
const linksChild = 'links';
const mailerChild = 'SMTP';
const mispChild = 'misp';
const observablesChild = 'observables';
const organisationChild = 'organisations';
const pagesChild = 'pages';
const passwordManageChild = 'password-manage';
const platformManagementChild = 'platform';
const platformStatusChild = 'status';
const proceduresChild = 'procedures';
const profilesChild = 'profiles';
const linkedAlertsChild = 'linked-alerts';
const reportsChild = 'reports';
const resetChild = 'reset';
const respondersChild = 'responders';
const settingsChild = 'settings';
const similarAlertsChild = 'similar-alerts';
const similarCasesChild = 'similar-cases';
const taskChild = 'tasks';
const taxonomiesChild = 'taxonomies';
const timelineChild = 'timeline';
const usersChild = 'users';
const viewsChild = 'views';
const infosChild = 'infos';
const passwordChild = 'password';
const apiChild = 'api';
const mfaChild = 'mfa';
const optionsChild = 'options';
const notificationsChild = 'notifications';
const endpointsChild = 'endpoints';
const ldapServersChild = 'ldap-servers';
const templatesChild = 'templates';

export const PATTERN_ROUTES = {
    cases: {
        // [cases] : /cases
        default: [caseRoot],

        // [cases.views] : /cases/views/:viewId
        views: [caseRoot, viewsChild, viewsPattern],

        // [cases.layout] : /cases/:caseId
        layout: [caseRoot, casePattern],

        // [cases.details] : /cases/:caseId/details
        details: [caseRoot, casePattern, detailsChild],

        // [cases.timeline] : /cases/:caseId/timeline
        timeline: [caseRoot, casePattern, timelineChild],

        // [cases.task] : /cases/:caseId/tasks/:taskId
        task: [caseRoot, casePattern, taskChild, taskPattern],

        // [cases.tasks] : /cases/:caseId/tasks
        tasks: [caseRoot, casePattern, taskChild],

        // [cases.observable] : /cases/:caseId/observables/:observableId
        observable: [caseRoot, casePattern, observablesChild, observablePattern],

        // [cases.observable-report] : /cases/:caseId/observables/:observableId/reports/:reportId
        'observable-report': [caseRoot, casePattern, observablesChild, observablePattern, reportsChild, reportsPattern],

        // [cases.observables] : /cases/:caseId/observables
        observables: [caseRoot, casePattern, observablesChild],

        // [cases.responders] : /cases/:caseId/responders
        responders: [caseRoot, casePattern, respondersChild],

        // [cases.procedures] : /cases/:caseId/procedures
        procedures: [caseRoot, casePattern, proceduresChild],

        // [cases.attachments] : /cases/:caseId/attachments
        attachments: [caseRoot, casePattern, attachmentsChild],

        // [cases.comments] : /cases/:caseId/comments
        comments: [caseRoot, casePattern, commentsChild],

        // [cases.attachments-section] : /cases/:caseId/attachments/:sectionId
        'attachments-section': [caseRoot, casePattern, attachmentsChild, sectionPattern],

        // [cases.similar-cases] : /cases/:caseId/similar-cases
        'similar-cases': [caseRoot, casePattern, similarCasesChild],

        // [cases.linked-alerts] : /cases/:caseId/linked-alerts
        'linked-alerts': [caseRoot, casePattern, linkedAlertsChild],

        // [cases.similar-alerts] : /cases/:caseId/similar-alerts
        'similar-alerts': [caseRoot, casePattern, similarAlertsChild],

        // [cases.page] : /cases/:caseId/pages/:pageId
        page: [caseRoot, casePattern, pagesChild, pagePattern],

        // [cases.pages] : /cases/:caseId/pages
        pages: [caseRoot, casePattern, pagesChild],

        // [cases.history] : /cases/:caseId/history
        history: [caseRoot, casePattern, historyChild],
    },
    organisation: {
        // [organisation.users] : /organisations/:orgId/users
        users: [organisationChild, organisationPattern, usersChild],

        // [organisation.templates] : /organisations/:orgId/templates/
        templates: [organisationChild, organisationPattern, templatesChild],

        // [organisation.templates-case] : //organisations/:orgId/templates/case
        'templates-case': [organisationChild, organisationPattern, templatesChild, 'case'],

        // [organisation.templates-report] : //organisations/:orgId/templates/report
        'templates-report': [organisationChild, organisationPattern, templatesChild, 'report'],

        // [organisation.templates-page] : //organisations/:orgId/templates/page
        'templates-page': [organisationChild, organisationPattern, templatesChild, 'page'],

        // [organisation.templates-report-details] : //organisations/:orgId/templates/report/:reportTemplateId
        'templates-report-details': [
            organisationChild,
            organisationPattern,
            templatesChild,
            'report',
            reportTemplatePattern,
        ],

        // [organisation.caseTemplates] : /organisations/:orgId/caseTemplates
        caseTemplates: [organisationChild, organisationPattern, caseTemplatesChild],

        // [organisation.customTags] : /organisations/:orgId/customTags
        customTags: [organisationChild, organisationPattern, customTagsChild],

        // [organisation.functions] : /organisations/:orgId/functions
        functions: [organisationChild, organisationPattern, functionsChild],

        // [organisation.links] : /organisations/:orgId/links
        links: [organisationChild, organisationPattern, linksChild],

        // [organisation.notifications] : /organisations/:orgId/notifications
        notifications: [organisationChild, organisationPattern, notificationsChild],

        // [organisation.endpoints] : /organisations/:orgId/endpoints
        endpoints: [organisationChild, organisationPattern, endpointsChild],

        // [organisation.settings] : /organisations/:orgId/settings
        settings: [organisationChild, organisationPattern, settingsChild],

        // [organisation.settings] : /organisations/:orgId/password-manage
        passwordManage: [organisationChild, organisationPattern, passwordManageChild],

        // [organisation.attachments] : /organisations/:orgId/attachments
        attachments: [organisationChild, organisationPattern, attachmentsChild],

        // [organisation.layout] : /organisations/:orgId
        layout: [organisationChild, organisationPattern],

        // [organisation.organizations] : /organisations
        organizations: [organisationChild],
    },
    administration: {
        // [administration.users] : /administration/organisations/:orgId/users
        users: [administrationRoot, organisationChild, organisationPattern, usersChild],

        // [administration.global-users] : /administration/global-users
        'global-users': [administrationRoot, globalUsersChild],

        // [administration.templates] : /administration/organisations/:orgId/templates/
        caseTemplate: [administrationRoot, organisationChild, organisationPattern, templatesChild],

        // [administration.caseTemplates] : /administration/organisations/:orgId/caseTemplates
        caseTemplates: [administrationRoot, organisationChild, organisationPattern, caseTemplatesChild],

        // [administration.customTags] : /administration/organisations/:orgId/customTags
        customTags: [administrationRoot, organisationChild, organisationPattern, customTagsChild],

        // [administration.functions] : /administration/organisations/:orgId/functions
        functions: [administrationRoot, organisationChild, organisationPattern, functionsChild],

        // [administration.links] : /administration/organisations/:orgId/links
        links: [administrationRoot, organisationChild, organisationPattern, linksChild],

        // [administration.notifications] : /administration/organisations/:orgId/notifications
        notifications: [administrationRoot, organisationChild, organisationPattern, notificationsChild],

        // [administration.endpoints] : /administration/organisations/:orgId/endpoints
        endpoints: [administrationRoot, organisationChild, organisationPattern, endpointsChild],

        // [administration.settings] : /administration/organisations/:orgId/settings
        settings: [administrationRoot, organisationChild, organisationPattern, settingsChild],

        // [administration.settings] : /administration/organisations/:orgId/password-manage
        passwordManage: [administrationRoot, organisationChild, organisationPattern, passwordManageChild],

        // [administration.layout] : /administration/organisations/:orgId
        layout: [administrationRoot, organisationChild, organisationPattern],

        // [administration.organizations] : /administration/organisations
        organizations: [administrationRoot, organisationChild],

        /** ENTITIES */
        // [administration.entities] : /administration/entities
        entities: [administrationRoot, entitiesManagementChild],

        // [administration.profiles] : /administration/entities/profiles
        profiles: [administrationRoot, entitiesManagementChild, profilesChild],

        // [administration.customFields] : /administration/entities/custom-fields
        customFields: [administrationRoot, entitiesManagementChild, customFieldsChild],

        // [administration.alert-status] : /administration/entities/alert-status
        'alert-status': [administrationRoot, entitiesManagementChild, alertStatusChild],

        // [administration.case-status] : /administration/entities/case-status
        'case-status': [administrationRoot, entitiesManagementChild, caseStatusChild],

        // [administration.observables] : /administration/entities/observables
        observables: [administrationRoot, entitiesManagementChild, observablesChild],

        // [administration.attackPatterns] : /administration/entities/attack-patterns
        attackPatterns: [administrationRoot, entitiesManagementChild, attackPatterns],

        // [administration.catalogPatterns] : /administration/entities/attack-patterns/:catalogId
        catalogPatterns: [administrationRoot, entitiesManagementChild, attackPatterns, catalogPattern],

        // [administration.taxonomies] : /administration/entities/taxonomies
        taxonomies: [administrationRoot, entitiesManagementChild, taxonomiesChild],

        // [administration.analyzerTemplates] : /administration/entities/analyzer-templates
        analyzerTemplates: [administrationRoot, entitiesManagementChild, analyzerTemplates],

        /** PLATFORM */
        // [administration.platform] : /administration/platform
        platform: [administrationRoot, platformManagementChild],

        // [administration.license] : /administration/platform/license
        license: [administrationRoot, platformManagementChild, licenseChild],

        // [administration.status] : /administration/platform/status
        status: [administrationRoot, platformManagementChild, platformStatusChild],

        // [administration.branding] : /administration/platform/branding
        branding: [administrationRoot, platformManagementChild, brandingChild],

        // [administration.cortex] : /administration/platform/cortex
        cortex: [administrationRoot, platformManagementChild, cortexChild],

        // [administration.misp] : /administration/platform/misp
        misp: [administrationRoot, platformManagementChild, mispChild],

        // [administration.authentication] : /administration/platform/authentication]
        authentication: [administrationRoot, platformManagementChild, authenticationChild],

        // [administration.mailer] : /administration/platform/SMTP
        mailer: [administrationRoot, platformManagementChild, mailerChild],

        // [administration.global-endpoints] : /administration/platform/endpoints
        'global-endpoints': [administrationRoot, platformManagementChild, endpointsChild],

        // [administration.global-endpoints] : /administration/platform/ldap-servers
        'ldap-servers': [administrationRoot, platformManagementChild, ldapServersChild],
    },
    alerts: {
        // [alerts] : /alerts
        default: [alertRoot],

        // [alerts.layout] : /alerts/:alertId
        layout: [alertRoot, alertPattern],

        // [alerts.details] : /alerts/:alertId/details
        details: [alertRoot, alertPattern, detailsChild],

        // [alerts.observable] : /alerts/:alertId/observables/:observableId
        observable: [alertRoot, alertPattern, observablesChild, observablePattern],

        // [alerts.observable-report] : /alerts/:alertId/observables/:observableId/reports/:reportId
        'observable-report': [
            alertRoot,
            alertPattern,
            observablesChild,
            observablePattern,
            reportsChild,
            reportsPattern,
        ],

        // [alerts.observables] : /alerts/:alertId/observables
        observables: [alertRoot, alertPattern, observablesChild],

        // [alerts.responders] : /alerts/:alertId/responders
        responders: [alertRoot, alertPattern, respondersChild],

        // [alerts.procedures] : /alerts/:alertId/procedures
        procedures: [alertRoot, alertPattern, proceduresChild],

        // [alerts.attachments] : /alerts/:alertId/attachments
        attachments: [alertRoot, alertPattern, attachmentsChild],

        // [alerts.comments] : /alerts/:caseId/comments
        comments: [alertRoot, alertPattern, commentsChild],

        // [alerts.similar-cases] : /alerts/:alertId/similar-cases
        'similar-cases': [alertRoot, alertPattern, similarCasesChild],

        // [alerts.similar-alerts] : /alerts/:alertId/similar-alerts
        'similar-alerts': [alertRoot, alertPattern, similarAlertsChild],

        // [alerts.history] : /alerts/:alertId/history
        history: [alertRoot, alertPattern, historyChild],
    },
    globalTasks: {
        // [globalTasks] : /global-tasks
        default: [globalTasksRoot],
    },
    myTasks: {
        // [myTasks] : /my-tasks
        default: [myTasksRoot],
    },
    waitingTasks: {
        // [waitingTasks] : /waiting-tasks
        default: [waitingTasksRoot],
    },
    account: {
        // [account.default]: /account/
        default: [accountRoot],

        // [account.infos] : /account/infos
        infos: [accountRoot, infosChild],

        // [account.password] : /account/password
        password: [accountRoot, passwordChild],

        // [account.api] : /account/api
        api: [accountRoot, apiChild],

        // [account.mfa] : /account/mfa
        mfa: [accountRoot, mfaChild],

        // [account.options] : /account/options
        options: [accountRoot, optionsChild],
    },
    dashboards: {
        // [dashboards] : /dashboards
        default: [dashboardsRoot],

        // [dashboards.details] : /dashboards/:dashboardId
        details: [dashboardsRoot, dashboardPattern],
    },
    live: {
        // [live.default] : /live
        default: [liveRoot],

        // [live.details] : /live/:caseId
        details: [liveRoot, casePattern],
    },
    search: {
        // [search] : /search
        default: [searchRoot],
    },
    login: {
        // [login] : /login
        default: [loginRoot],

        // [login.change] : /login/reset-password/:secret
        change: [loginRoot, changeChild, patternSecret],

        // [login.reset] : /login/reset
        reset: [loginRoot, resetChild],
    },
};

export type PatternType = keyof Flatten<typeof PATTERN_ROUTES>;

export const PATTERN_ROUTES_FLAT = [...Object.keys(flatten(PATTERN_ROUTES, { safe: true }))] as const;
